import React, {useImperativeHandle} from "react";
import {useEditor, EditorContent} from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Image from "@tiptap/extension-image";
import {C} from "../styles";
import {useToast, usePrompt} from "./FeedbackProvider";

const EXTENSIONS = [
    StarterKit.configure({
        heading: {levels: [2, 3]},
        strike: false,
        code: false,
        codeBlock: false,
        blockquote: false,
        horizontalRule: false,
        link: false,
        underline: false,
    }),
    Link.configure({openOnClick: false, autolink: false, protocols: ["http", "https"]}),
    Image.configure({inline: false}),
];

function ToolbarButton({title, active, onClick, children}) {
    return (
        <button
            type="button"
            title={title}
            onClick={onClick}
            style={{
                border: `1px solid ${active ? C.primary : C.border}`,
                backgroundColor: active ? C.primaryLight : "#fff",
                borderRadius: 6,
                padding: "6px 10px",
                marginRight: 4,
                marginBottom: 4,
                cursor: "pointer",
                fontSize: 13,
            }}
        >
            {children}
        </button>
    );
}

// useEditor ne lit `initialContent` qu'au montage : un remplacement ultérieur du document (insertion
// d'un brouillon IA) passe par le handle `ref.setContent`. Le parent tient déjà ce contenu dans son
// état, d'où emitUpdate: false (pas d'onChange en écho).
export default function NewsRichTextEditor({initialContent, onChange, onRequestImageUpload, ref}) {
    const {notify} = useToast();
    const prompt = usePrompt();
    const editor = useEditor({
        extensions: EXTENSIONS,
        content: initialContent || {type: "doc", content: [{type: "paragraph"}]},
        onUpdate: ({editor: ed}) => onChange(ed.getJSON()),
    });

    useImperativeHandle(ref, () => ({
        setContent: (content) => editor?.commands.setContent(content, {emitUpdate: false}),
    }), [editor]);

    if (!editor) return null;

    async function handleImageClick() {
        const result = await onRequestImageUpload();
        if (result && result.url) {
            editor.chain().focus().setImage({src: result.url, alt: result.alt || ""}).run();
        }
    }

    async function handleLinkClick() {
        const url = await prompt("Adresse du lien (https://...)");
        if (!url) return;
        if (!/^https?:\/\//.test(url)) {
            notify("Le lien doit commencer par http:// ou https://", "error");
            return;
        }
        editor.chain().focus().setLink({href: url}).run();
    }

    return (
        <div style={{border: `1px solid ${C.border}`, borderRadius: 10, padding: 10}}>
            <div style={{marginBottom: 8}}>
                <ToolbarButton title="Titre niveau 2" active={editor.isActive("heading", {level: 2})} onClick={() => editor.chain().focus().toggleHeading({level: 2}).run()}>H2</ToolbarButton>
                <ToolbarButton title="Titre niveau 3" active={editor.isActive("heading", {level: 3})} onClick={() => editor.chain().focus().toggleHeading({level: 3}).run()}>H3</ToolbarButton>
                <ToolbarButton title="Gras" active={editor.isActive("bold")} onClick={() => editor.chain().focus().toggleBold().run()}><strong>G</strong></ToolbarButton>
                <ToolbarButton title="Italique" active={editor.isActive("italic")} onClick={() => editor.chain().focus().toggleItalic().run()}><em>I</em></ToolbarButton>
                <ToolbarButton title="Liste à puces" active={editor.isActive("bulletList")} onClick={() => editor.chain().focus().toggleBulletList().run()}>• Liste</ToolbarButton>
                <ToolbarButton title="Liste numérotée" active={editor.isActive("orderedList")} onClick={() => editor.chain().focus().toggleOrderedList().run()}>1. Liste</ToolbarButton>
                <ToolbarButton title="Lien" active={editor.isActive("link")} onClick={handleLinkClick}>🔗</ToolbarButton>
                <ToolbarButton title="Image" onClick={handleImageClick}>🖼️</ToolbarButton>
                <ToolbarButton title="Annuler" onClick={() => editor.chain().focus().undo().run()}>↺</ToolbarButton>
                <ToolbarButton title="Rétablir" onClick={() => editor.chain().focus().redo().run()}>↻</ToolbarButton>
            </div>
            <EditorContent editor={editor} style={{minHeight: 220}}/>
        </div>
    );
}
